import { useRef, useState, useCallback } from "react";
import { Modal, Alert, Button, Spin } from "antd";
import { Html5Qrcode } from "html5-qrcode";
import { ConnectScanResult, parseConnectTrackerScan } from "../utils/connectScanParser";

const SCANNER_ELEMENT_ID = "connect-tracker-qr-reader";

interface ConnectTrackerScannerProps {
  visible: boolean;
  onClose: () => void;
  onScan: (result: ConnectScanResult) => void;
}

export default function ConnectTrackerScanner({
  visible,
  onClose,
  onScan,
}: ConnectTrackerScannerProps) {
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const scanner = scannerRef.current;
        scannerRef.current = null;
        if (scanner.isScanning) {
          await scanner.stop();
        }
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setIsScanning(false);
    setIsStarting(false);
  }, []);

  const handleScanResult = useCallback(
    (decodedText: string) => {
      const result = parseConnectTrackerScan(decodedText);
      if (!result) {
        setError("Neplatný QR kód.");
        return;
      }
      void stopScanner();
      onScan(result);
    },
    [onScan, stopScanner]
  );

  const startScanner = useCallback(async () => {
    if (scannerRef.current) return;

    const element = document.getElementById(SCANNER_ELEMENT_ID);
    if (!element) {
      console.error("Connect tracker QR reader element not found");
      return;
    }

    setError(null);
    setIsStarting(true);

    try {
      const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          handleScanResult(decodedText);
        },
        () => {}
      );
      setIsScanning(true);
    } catch (err) {
      console.error("Connect tracker QR Scanner error:", err);
      setError("Nepodařilo se spustit kameru. Zkontrolujte oprávnění.");
      scannerRef.current = null;
    } finally {
      setIsStarting(false);
    }
  }, [handleScanResult]);

  const handleAfterOpenChange = (open: boolean) => {
    if (open) {
      setTimeout(() => {
        void startScanner();
      }, 300);
    }
  };

  const handleClose = () => {
    void stopScanner();
    onClose();
  };

  return (
    <Modal
      title="Naskenujte QR kód"
      open={visible}
      onCancel={handleClose}
      afterOpenChange={handleAfterOpenChange}
      footer={<Button onClick={handleClose}>Zavřít</Button>}
      destroyOnClose
      width={350}
      centered
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ width: "100%" }}
          />
        )}

        <div
          id={SCANNER_ELEMENT_ID}
          style={{
            width: "100%",
            maxWidth: 300,
            minHeight: 300,
            background: "#000",
            borderRadius: 8,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isStarting && !isScanning && <Spin size="large" />}
        </div>

        {isScanning && (
          <p style={{ margin: 0, color: "#666", textAlign: "center" }}>
            Tracker (číslo), case ID, uživatel nebo místo
          </p>
        )}

        {isStarting && !isScanning && (
          <p style={{ margin: 0, color: "#666", textAlign: "center" }}>
            Spouštění kamery...
          </p>
        )}
      </div>
    </Modal>
  );
}
